import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft, Download, Pencil, Trash2, Link as LinkIcon, X, FolderKanban, FileText, Share2, Plug,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useDeleteProject, usePatchProject } from "@/hooks/use-projects-mutations";
import { useProjectProfiles, useLinkProjectProfile, useUnlinkProjectProfile } from "@/hooks/use-project-profiles";
import { useActiveProject } from "@/hooks/use-active-project";
import { formatNumber } from "@/lib/utils";
import { NetworkQueryEditor } from "@/components/feature/network-query-editor";
import { SocialDot } from "@/components/feature/social-dot";

export const Route = createFileRoute("/_auth/monitor/$projectId")({
  component: MonitorDetail,
});

const NETWORK_LABEL: Record<string, string> = {
  x: "X", ig: "Instagram", fb: "Facebook", li: "LinkedIn",
  yt: "YouTube", tt: "TikTok", news: "Noticias", reddit: "Reddit",
};

function MonitorDetail() {
  const { projectId } = Route.useParams();
  const id = Number(projectId);
  const { setActiveId } = useActiveProject();
  useEffect(() => {
    if (Number.isFinite(id)) setActiveId(id);
  }, [id, setActiveId]);

  const project = useQuery({
    queryKey: ["projects", id],
    queryFn: () => api.getProject(id),
  });

  const mentions = useQuery({
    queryKey: ["mentions", { project_id: id }],
    queryFn: () => api.listMentions({ project_id: id, limit: 50 }),
  });

  const [editing, setEditing] = useState(false);
  const deleteProject = useDeleteProject();

  const networksCount = (project.data?.networks ?? []).length;
  const queriesCount = Object.values(project.data?.network_queries ?? {}).filter((v) => v.trim()).length;
  const lastActivity = project.data?.last_mention_at;

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <Link to="/monitor" className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-brand-600 mb-1">
              <ArrowLeft size={12} /> Proyectos
            </Link>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300 flex items-center justify-center"
                style={project.data?.color ? { backgroundColor: `${project.data.color}1A`, color: project.data.color } : undefined}
              >
                <FolderKanban size={18} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-ink-900 dark:text-white">
                  {project.data?.name ?? "Cargando…"}
                </h1>
                {project.data && (
                  <p className="text-sm text-ink-500">
                    {project.data.description || "Sin descripción"} · Creado el{" "}
                    {new Date(project.data.created_at).toLocaleDateString("es-PE")}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/monitor/$projectId/setup" params={{ projectId: String(id) }}>
              <Button size="sm">
                <Plug size={14} /> Configurar fuentes
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Editar
            </Button>
            <Button variant="outline" size="sm" disabled>
              <Share2 size={14} /> Compartir
            </Button>
            <a href={api.exportMentionsUrl({ project_id: id })} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm">
                <Download size={14} /> Exportar CSV
              </Button>
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm(`¿Eliminar el proyecto "${project.data?.name}"? Esto borrará también todas sus menciones asociadas (no recuperable).`)) {
                  deleteProject.mutate(id);
                }
              }}
              className="text-bad hover:bg-bad/10"
            >
              <Trash2 size={14} /> Borrar
            </Button>
          </div>
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryStat label="Menciones recolectadas" value={formatNumber(project.data?.mentions_count ?? 0)} icon={FileText} />
          <SummaryStat label="Redes monitoreadas" value={String(networksCount)} sub={`${(project.data?.networks ?? []).join(", ") || "—"}`} />
          <SummaryStat label="Boolean queries" value={String(queriesCount)} sub={`${networksCount} configurables`} />
          <SummaryStat
            label="Última actividad"
            value={lastActivity ? new Date(lastActivity).toLocaleDateString("es-PE") : "—"}
            sub={lastActivity ? new Date(lastActivity).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" }) : "Sin menciones"}
          />
        </div>
      </header>

      {editing && project.data && (
        <EditProjectModal
          id={id}
          initial={{
            name: project.data.name,
            description: project.data.description ?? "",
            networks: project.data.networks ?? [],
            network_queries: project.data.network_queries ?? {},
          }}
          onClose={() => setEditing(false)}
        />
      )}

      {project.data && <ProjectBindingsCard projectId={id} accountProjectNetworks={project.data.networks} />}

      <Card className="!p-0 overflow-hidden">
        <CardHeader className="px-5 py-4 border-b border-ink-100 dark:border-ink-800">
          <CardTitle>Menciones recolectadas</CardTitle>
          <CardDescription>
            {project.data ? `${formatNumber(project.data.mentions_count)} totales` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent className="!p-0">
          {mentions.isLoading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : !mentions.data || mentions.data.length === 0 ? (
            <p className="text-sm text-ink-500 py-12 text-center">
              Sin menciones aún. Los workers de captura aún no detectaron contenido para este proyecto.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-500 border-b border-ink-100 dark:border-ink-800">
                  <th className="pl-5 pr-3 py-2.5 font-medium">Mención</th>
                  <th className="px-3 py-2.5 font-medium">Canal</th>
                  <th className="px-3 py-2.5 font-medium">Sentimiento</th>
                  <th className="px-3 py-2.5 font-medium">Alcance</th>
                  <th className="px-3 py-2.5 font-medium">Fecha</th>
                  <th className="pr-5" />
                </tr>
              </thead>
              <tbody>
                {mentions.data.map((m) => (
                  <tr key={m.id} className="border-b border-ink-50 dark:border-ink-800/60 hover:bg-ink-50/60 dark:hover:bg-ink-800/30">
                    <td className="pl-5 pr-3 py-3">
                      <div className="flex items-start gap-3 max-w-2xl">
                        <div className="w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 flex items-center justify-center text-[10px] font-semibold shrink-0">
                          {(m.author_handle ?? "?").slice(1, 3).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-ink-900 dark:text-white">{m.author_handle ?? "—"}</span>
                          </div>
                          <p className="text-xs text-ink-600 dark:text-ink-300 line-clamp-2 mt-0.5">{m.text}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <SocialDot network={m.network} size={20} />
                        <span className="capitalize text-ink-700 dark:text-ink-200 text-xs">
                          {NETWORK_LABEL[m.network] ?? m.network}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <Badge variant={m.sentiment === "pos" ? "good" : m.sentiment === "neg" ? "bad" : "muted"}>
                        {m.sentiment === "pos" ? "Positivo" : m.sentiment === "neg" ? "Negativo" : "Neutral"}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-ink-700 dark:text-ink-200 text-xs">{formatNumber(m.reach)}</td>
                    <td className="px-3 py-3 text-ink-500 text-xs whitespace-nowrap">
                      {new Date(m.published_at).toLocaleString("es-PE", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="pr-5 py-3 text-right">
                      {m.url && (
                        <a
                          href={m.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-600 hover:text-brand-700 text-xs"
                        >
                          Ver →
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const editSchema = z.object({
  name: z.string().min(2).max(160),
  description: z.string().max(500).optional(),
});
type EditFormInput = z.infer<typeof editSchema>;
type EditInput = EditFormInput & { networks: string[]; network_queries: Record<string, string> };

function EditProjectModal({
  id, initial, onClose,
}: { id: number; initial: EditInput; onClose: () => void }) {
  const patch = usePatchProject(id);
  const form = useForm<EditFormInput>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: initial.name, description: initial.description },
  });
  const [networks, setNetworks] = useState<string[]>(initial.networks);
  const [networkQueries, setNetworkQueries] = useState<Record<string, string>>(initial.network_queries);

  async function onSubmit(data: EditFormInput) {
    try {
      await patch.mutateAsync({ ...data, networks, network_queries: networkQueries });
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4 overflow-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader>
          <CardTitle>Editar proyecto</CardTitle>
          <CardDescription>Ajusta nombre, redes monitoreadas y boolean queries por red.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" {...form.register("name")} />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input className="mt-1" {...form.register("description")} />
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
              <NetworkQueryEditor
                networks={networks}
                networkQueries={networkQueries}
                onChange={(n, q) => { setNetworks(n); setNetworkQueries(q); }}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={patch.isPending}>
                {patch.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}


function SummaryStat({
  label, value, sub, icon: Icon,
}: { label: string; value: string; sub?: string; icon?: typeof FileText }) {
  return (
    <Card className="flex flex-col gap-1">
      <div className="flex items-start justify-between">
        <p className="text-xs text-ink-500">{label}</p>
        {Icon && <Icon size={14} className="text-ink-400" />}
      </div>
      <h2 className="text-2xl font-bold text-ink-900 dark:text-white">{value}</h2>
      {sub && <p className="text-[11px] text-ink-500 line-clamp-1">{sub}</p>}
    </Card>
  );
}


function ProjectBindingsCard({ projectId, accountProjectNetworks }: { projectId: number; accountProjectNetworks: string[] }) {
  const bound = useProjectProfiles(projectId);
  const allProfiles = useQuery({
    queryKey: ["profiles", "analytic"],
    queryFn: () => api.listProfiles(),
    staleTime: 60_000,
  });
  const link = useLinkProjectProfile(projectId);
  const unlink = useUnlinkProjectProfile(projectId);

  const boundIds = new Set((bound.data ?? []).map((p) => p.id));
  const available = (allProfiles.data ?? []).filter(
    (p) => p.kind === "analytic" && !boundIds.has(p.id) && accountProjectNetworks.includes(p.network),
  );

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Cuentas vinculadas</CardTitle>
        <CardDescription>Perfiles RRSS analíticos usados por workers para este proyecto.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {(bound.data ?? []).length === 0 ? (
            <p className="text-sm text-ink-500">Sin perfiles vinculados. Los workers usarán credenciales globales como fallback.</p>
          ) : (
            (bound.data ?? []).map((pr) => (
              <div key={pr.id} className="flex items-center gap-2 py-1">
                <Badge>{pr.network}</Badge>
                <span className="text-sm flex-1">{pr.handle}</span>
                <button
                  type="button"
                  className="text-xs text-bad hover:underline"
                  onClick={() => unlink.mutate(pr.id)}
                >
                  <X size={12} className="inline" /> desvincular
                </button>
              </div>
            ))
          )}
        </div>
        {available.length > 0 && (
          <div className="border-t border-ink-200 dark:border-ink-800 pt-3 mt-3">
            <p className="text-xs text-ink-500 mb-2">Vincular perfil existente:</p>
            <div className="flex flex-wrap gap-2">
              {available.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => link.mutate(p.id)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-ink-200 dark:border-ink-700 text-xs hover:bg-ink-50 dark:hover:bg-ink-800"
                >
                  <LinkIcon size={11} />
                  <Badge variant="muted">{p.network}</Badge>
                  {p.handle}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
